import {
    AlertTriangle,
    Archive,
    ArrowRight,
    CheckCircle2,
    ChevronRight,
    FileText,
    LoaderCircle,
    Plus,
    RefreshCw,
    Sparkles,
    Tag,
    Users,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './SalaryStructureScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatCost = (template) => {
    if (template.componentCount === 0) return null
    if (template.estimatedMonthlyCost > 0) {
        return `Est. \u20b9${Number(template.estimatedMonthlyCost).toLocaleString('en-IN')}${template.isEstimate ? '*' : ''}/mo`
    }
    return null
}

const SalaryStructureScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [structureData, setStructureData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [showArchived, setShowArchived] = useState(false)

    const fetchSalaryStructureList = useCallback(
        async (includeArchived = false, { silent = false } = {}) => {
            if (!authCtx?.token || !storeIdentifier) {
                if (!silent) setIsLoading(false)
                return
            }
            if (!silent) setIsLoading(true)
            else setIsRefreshing(true)

            try {
                const response = await fetch(BASE_URL_API + 'getStoreSalaryStructureList', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier,
                        showArchived: includeArchived,
                    }),
                })

                if (!response.ok) {
                    toast.error('Failed to load salary structures.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setStructureData(respo.response)
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                toast.error(respo?.message || 'Failed to load salary structures.')
            } catch (error) {
                console.error('fetchSalaryStructureList error:', error)
                toast.error('Network error. Please check your connection.')
            } finally {
                setIsLoading(false)
                setIsRefreshing(false)
            }
        },
        [authCtx, storeIdentifier]
    )

    // Initial + showArchived toggle load
    useEffect(() => {
        fetchSalaryStructureList(showArchived)
    }, [fetchSalaryStructureList, showArchived])

    // Refresh on route key change (back from create / detail screens)
    useEffect(() => {
        if (!storeIdentifier) return
        fetchSalaryStructureList(showArchived, { silent: true })
    }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

    // Refresh on window focus / tab visibility
    useEffect(() => {
        const onFocus = () => {
            if (!storeIdentifier) return
            fetchSalaryStructureList(showArchived, { silent: true })
        }
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onFocus()
        }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [fetchSalaryStructureList, showArchived, storeIdentifier])

    const templates = useMemo(
        () => Array.isArray(structureData?.templates) ? structureData.templates : [],
        [structureData]
    )

    const hasEstimate = templates.some((t) => t.isEstimate)
    const isEmpty = !isLoading && templates.length === 0

    const goToCreate = () => navigate('/payroll/salary-structure/create')

    const goToDetail = (templateIdentifier, templateName) => {
        navigate(`/payroll/salary-structure/${templateIdentifier}`, {
            state: { templateIdentifier, templateName },
        })
    }

    return (
        <div className={styles.page}>

            {/* ── Hero ── */}
            <section className={styles.hero}>
                <div className={styles.heroTopRow}>
                    <span className={styles.heroPill}><Sparkles size={14} /> Salary Structures</span>
                    {isRefreshing && (
                        <span className={styles.refreshHint}>
                            <RefreshCw size={13} className={styles.spin} /> Refreshing…
                        </span>
                    )}
                </div>
                <div className={styles.heroMain}>
                    <div>
                        <h1>Salary Templates</h1>
                        <p>Create and manage reusable salary blueprints. Assign them to staff to drive payroll calculations.</p>
                    </div>
                    <button type="button" className={styles.createBtn} onClick={goToCreate}>
                        <Plus size={15} /> Create Template
                    </button>
                </div>
            </section>

            {/* ── No store guard ── */}
            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store to continue.</strong>
                        <p>Salary structures are store-specific.</p>
                    </div>
                </section>
            )}

            {/* ── Summary row ── */}
            {structureData && (
                <section className={styles.summaryRow}>
                    <div className={styles.summaryPills}>
                        <span className={styles.summaryPillActive}>
                            <CheckCircle2 size={13} /> {structureData.activeCount} Active
                        </span>
                        {structureData.archivedCount > 0 && (
                            <span className={styles.summaryPillArchived}>
                                <Archive size={13} /> {structureData.archivedCount} Archived
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className={`${styles.archiveToggle} ${showArchived ? styles.archiveToggleOn : ''}`}
                        onClick={() => setShowArchived((prev) => !prev)}
                    >
                        {showArchived ? 'Hide Archived' : 'Show Archived'}
                    </button>
                </section>
            )}

            {/* ── Loading ── */}
            {isLoading && (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={22} className={styles.spin} />
                    <p>Loading salary structures…</p>
                </section>
            )}

            {/* ── Empty state ── */}
            {isEmpty && (
                <section className={styles.emptyState}>
                    <div className={styles.emptyIllo}>
                        <FileText size={36} className={styles.emptyIlloIcon} />
                    </div>
                    <h3>No salary structures yet</h3>
                    <p>Create a template to define fixed salary components for your staff tiers.</p>
                    <button type="button" className={styles.emptyCtaBtn} onClick={goToCreate}>
                        Create First Template <ArrowRight size={15} />
                    </button>
                </section>
            )}

            {/* ── Template list ── */}
            {!isLoading && templates.length > 0 && (
                <section className={styles.listWrap}>
                    {templates.map((template) => {
                        const isIncomplete = template.componentCount === 0
                        const missingBasic = template.componentCount > 0 && !template.hasBasicComponent
                        const isArchived = template.isActive === 0
                        const costDisplay = formatCost(template)

                        return (
                            <article
                                key={template.templateIdentifier}
                                className={`${styles.templateCard} ${isArchived ? styles.templateCardArchived : ''}`}
                                onClick={() => goToDetail(template.templateIdentifier, template.templateName)}
                            >
                                <div className={styles.cardMain}>
                                    <div className={styles.cardLeft}>
                                        <div className={styles.cardNameRow}>
                                            <span className={styles.templateName}>{template.templateName}</span>
                                            {template.designationTag && (
                                                <span className={styles.tagChip}>
                                                    <Tag size={11} /> {template.designationTag}
                                                </span>
                                            )}
                                            {isArchived && (
                                                <span className={styles.archivedBadge}>
                                                    <Archive size={11} /> Archived
                                                </span>
                                            )}
                                        </div>

                                        {template.templateDescription && (
                                            <p className={styles.templateDesc}>{template.templateDescription}</p>
                                        )}

                                        <div className={styles.statsRow}>
                                            <span className={styles.statItem}>
                                                <FileText size={12} />
                                                {template.componentCount} component{template.componentCount !== 1 ? 's' : ''}
                                            </span>
                                            <span className={styles.statDot} />
                                            <span className={styles.statItem}>
                                                <Users size={12} />
                                                {template.staffAssignedCount} staff
                                            </span>
                                            {costDisplay && (
                                                <>
                                                    <span className={styles.statDot} />
                                                    <span className={styles.statItemCost}>{costDisplay}</span>
                                                </>
                                            )}
                                        </div>

                                        {isIncomplete && (
                                            <div className={styles.warnBadge}>
                                                <AlertTriangle size={12} />
                                                No components added yet — tap to add
                                            </div>
                                        )}
                                        {missingBasic && (
                                            <div className={styles.warnBadge}>
                                                <AlertTriangle size={12} />
                                                BASIC component missing — template not usable
                                            </div>
                                        )}
                                    </div>

                                    <ChevronRight size={18} className={styles.cardChevron} />
                                </div>
                            </article>
                        )
                    })}

                    {hasEstimate && (
                        <p className={styles.estimateNote}>
                            * Estimated cost is approximate. Templates with percentage-based components
                            may vary per staff based on their basic salary.
                        </p>
                    )}
                </section>
            )}

            {/* ── FAB (mobile) ── */}
            <button type="button" className={styles.fab} onClick={goToCreate}>
                <Plus size={17} /> Create Template
            </button>
        </div>
    )
}

export default SalaryStructureScreen